import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Skeleton,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  Language as LanguageIcon,
  CheckCircle as CheckCircleIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { subscriberService, whatsappService } from '../../services/api';

const SubscriberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [subscriber, setSubscriber] = useState(null);
  const [messageHistory, setMessageHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriber();
  }, [id]);

  const fetchSubscriber = async () => {
    try {
      const [subResponse, historyResponse] = await Promise.all([
        subscriberService.getById(id),
        whatsappService.getSubscriberHistory(id, 20),
      ]);
      
      setSubscriber(subResponse.data.data);
      setMessageHistory(historyResponse.data.data);
    } catch (error) {
      enqueueSnackbar('Erreur lors du chargement', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    try {
      await subscriberService.validate(id);
      enqueueSnackbar('Abonné validé avec succès', { variant: 'success' });
      fetchSubscriber();
    } catch (error) {
      enqueueSnackbar('Erreur lors de la validation', { variant: 'error' });
    }
  };

  const getStatusChip = (status) => {
    const config = {
      active: { color: 'success', label: 'Actif' },
      pending: { color: 'warning', label: 'En attente' },
      inactive: { color: 'default', label: 'Inactif' },
      blocked: { color: 'error', label: 'Bloqué' },
    };
    const { color, label } = config[status] || { color: 'default', label: status };
    return <Chip label={label} color={color} />;
  };

  if (loading) {
    return (
      <Box>
        <Skeleton height={60} />
        <Skeleton height={300} />
      </Box>
    );
  }

  if (!subscriber) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Abonné non trouvé</Typography>
        <Button onClick={() => navigate('/subscribers')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/subscribers')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Détails de l'abonné
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Informations principales */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mr: 2 
                  }}
                >
                  {subscriber.first_name?.[0] || subscriber.phone_number[0]}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {subscriber.first_name || 'Non renseigné'} {subscriber.last_name || ''}
                  </Typography>
                  {getStatusChip(subscriber.status)}
                </Box>
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Numéro WhatsApp" 
                    secondary={`${subscriber.country_code}${subscriber.phone_number}`}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <LanguageIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Langue préférée" 
                    secondary={subscriber.preferredLanguage?.name || 'Français'}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Date d'inscription" 
                    secondary={new Date(subscriber.subscription_date).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  />
                </ListItem>
                
                {subscriber.validated_at && (
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Validé le" 
                      secondary={`${new Date(subscriber.validated_at).toLocaleDateString('fr-FR')} par ${subscriber.validator?.first_name || 'Admin'}`}
                    />
                  </ListItem>
                )}
                
                <ListItem>
                  <ListItemIcon>
                    <WhatsAppIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Messages reçus" 
                    secondary={subscriber.total_messages_received}
                  />
                </ListItem>
              </List>

              {subscriber.status === 'pending' && (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<CheckCircleIcon />}
                  onClick={handleValidate}
                  sx={{ mt: 2 }}
                >
                  Valider cet abonné
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Historique des messages */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon color="success" />
                Historique des messages
              </Typography>
              
              {messageHistory.length > 0 ? (
                <List>
                  {messageHistory.map((msg, index) => (
                    <React.Fragment key={msg.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">
                                {new Date(msg.ephemeris?.date).toLocaleDateString('fr-FR')}
                              </Typography>
                              <Chip 
                                label={msg.status === 'sent' || msg.status === 'delivered' ? 'Envoyé' : msg.status}
                                color={msg.status === 'failed' ? 'error' : 'success'}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={msg.sent_at ? new Date(msg.sent_at).toLocaleString('fr-FR') : 'En attente'}
                        />
                      </ListItem>
                      {index < messageHistory.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Aucun message envoyé
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscriberDetail;
